export type ContextFamily =
    | 'background'
    | 'card'
    | 'popover'
    | 'accent'
    | 'navigation'
    | 'primary'
    | 'secondary'
    | 'destructive'
    | 'caution'
    | 'warning'
    | 'urgent'
    | 'info'
    | 'success';

export type ContextTone = 'default' | 'muted';
export type ContextRole = 'surface' | 'foreground' | 'decorative';

export interface ContextSelection {
    readonly family: ContextFamily;
    readonly tone?: ContextTone;
}

export interface ContextStyle<ClassName extends string> {
    readonly variable: `--color-${string}`;
    readonly className: ClassName;
}

export interface ContextColors {
    readonly surface: ContextStyle<`bg-${string}`>;
    readonly foreground: ContextStyle<`text-${string}`>;
    readonly decorative: ContextStyle<`text-${string}`>;
}

/** Explicit role mappings keep Tailwind source scanning and canonical tokens aligned. */
export const CONTEXT_COLORS = {
    background: {
        default: {
            surface: {
                variable: '--color-background',
                className: 'bg-background',
            },
            foreground: {
                variable: '--color-foreground',
                className: 'text-foreground',
            },
            decorative: {
                variable: '--color-foreground',
                className: 'text-foreground',
            },
        },
        muted: {
            surface: {
                variable: '--color-background',
                className: 'bg-background',
            },
            foreground: {
                variable: '--color-foreground',
                className: 'text-foreground',
            },
            decorative: {
                variable: '--color-foreground',
                className: 'text-foreground',
            },
        },
    },
    card: {
        default: {
            surface: { variable: '--color-card', className: 'bg-card' },
            foreground: {
                variable: '--color-card-foreground',
                className: 'text-card',
            },
            decorative: {
                variable: '--color-card-foreground',
                className: 'text-card',
            },
        },
        muted: {
            surface: { variable: '--color-card', className: 'bg-card' },
            foreground: {
                variable: '--color-card-foreground',
                className: 'text-card',
            },
            decorative: {
                variable: '--color-card-foreground',
                className: 'text-card',
            },
        },
    },
    popover: {
        default: {
            surface: { variable: '--color-popover', className: 'bg-popover' },
            foreground: {
                variable: '--color-popover-foreground',
                className: 'text-popover',
            },
            decorative: {
                variable: '--color-popover-foreground',
                className: 'text-popover',
            },
        },
        muted: {
            surface: { variable: '--color-popover', className: 'bg-popover' },
            foreground: {
                variable: '--color-popover-foreground',
                className: 'text-popover',
            },
            decorative: {
                variable: '--color-popover-foreground',
                className: 'text-popover',
            },
        },
    },
    accent: {
        default: {
            surface: { variable: '--color-accent', className: 'bg-accent' },
            foreground: {
                variable: '--color-accent-foreground',
                className: 'text-accent',
            },
            decorative: {
                variable: '--color-accent-foreground',
                className: 'text-accent',
            },
        },
        muted: {
            surface: { variable: '--color-accent', className: 'bg-accent' },
            foreground: {
                variable: '--color-accent-foreground',
                className: 'text-accent',
            },
            decorative: {
                variable: '--color-accent-foreground',
                className: 'text-accent',
            },
        },
    },
    navigation: {
        default: {
            surface: {
                variable: '--color-navigation',
                className: 'bg-navigation',
            },
            foreground: {
                variable: '--color-navigation-foreground',
                className: 'text-navigation',
            },
            decorative: {
                variable: '--color-navigation-foreground',
                className: 'text-navigation',
            },
        },
        muted: {
            surface: {
                variable: '--color-navigation',
                className: 'bg-navigation',
            },
            foreground: {
                variable: '--color-navigation-foreground',
                className: 'text-navigation',
            },
            decorative: {
                variable: '--color-navigation-foreground',
                className: 'text-navigation',
            },
        },
    },
    primary: {
        default: {
            surface: { variable: '--color-primary', className: 'bg-primary' },
            foreground: {
                variable: '--color-primary-foreground',
                className: 'text-primary',
            },
            decorative: {
                variable: '--color-primary-decorative',
                className: 'text-primary-decorative',
            },
        },
        muted: {
            surface: {
                variable: '--color-primary-muted',
                className: 'bg-primary-muted',
            },
            foreground: {
                variable: '--color-primary-muted-foreground',
                className: 'text-primary-muted',
            },
            decorative: {
                variable: '--color-primary-muted-decorative',
                className: 'text-primary-muted-decorative',
            },
        },
    },
    secondary: {
        default: {
            surface: {
                variable: '--color-secondary',
                className: 'bg-secondary',
            },
            foreground: {
                variable: '--color-secondary-foreground',
                className: 'text-secondary',
            },
            decorative: {
                variable: '--color-secondary-decorative',
                className: 'text-secondary-decorative',
            },
        },
        muted: {
            surface: { variable: '--color-muted', className: 'bg-muted' },
            foreground: {
                variable: '--color-muted-foreground',
                className: 'text-muted',
            },
            decorative: {
                variable: '--color-muted-decorative',
                className: 'text-muted-decorative',
            },
        },
    },
    destructive: {
        default: {
            surface: {
                variable: '--color-destructive',
                className: 'bg-destructive',
            },
            foreground: {
                variable: '--color-destructive-foreground',
                className: 'text-destructive',
            },
            decorative: {
                variable: '--color-destructive-decorative',
                className: 'text-destructive-decorative',
            },
        },
        muted: {
            surface: {
                variable: '--color-destructive-muted',
                className: 'bg-destructive-muted',
            },
            foreground: {
                variable: '--color-destructive-muted-foreground',
                className: 'text-destructive-muted',
            },
            decorative: {
                variable: '--color-destructive-muted-decorative',
                className: 'text-destructive-muted-decorative',
            },
        },
    },
    caution: {
        default: {
            surface: { variable: '--color-caution', className: 'bg-caution' },
            foreground: {
                variable: '--color-caution-foreground',
                className: 'text-caution',
            },
            decorative: {
                variable: '--color-caution-decorative',
                className: 'text-caution-decorative',
            },
        },
        muted: {
            surface: {
                variable: '--color-caution-muted',
                className: 'bg-caution-muted',
            },
            foreground: {
                variable: '--color-caution-muted-foreground',
                className: 'text-caution-muted',
            },
            decorative: {
                variable: '--color-caution-muted-decorative',
                className: 'text-caution-muted-decorative',
            },
        },
    },
    warning: {
        default: {
            surface: { variable: '--color-warning', className: 'bg-warning' },
            foreground: {
                variable: '--color-warning-foreground',
                className: 'text-warning',
            },
            decorative: {
                variable: '--color-warning-decorative',
                className: 'text-warning-decorative',
            },
        },
        muted: {
            surface: {
                variable: '--color-warning-muted',
                className: 'bg-warning-muted',
            },
            foreground: {
                variable: '--color-warning-muted-foreground',
                className: 'text-warning-muted',
            },
            decorative: {
                variable: '--color-warning-muted-decorative',
                className: 'text-warning-muted-decorative',
            },
        },
    },
    urgent: {
        default: {
            surface: { variable: '--color-urgent', className: 'bg-urgent' },
            foreground: {
                variable: '--color-urgent-foreground',
                className: 'text-urgent',
            },
            decorative: {
                variable: '--color-urgent-decorative',
                className: 'text-urgent-decorative',
            },
        },
        muted: {
            surface: {
                variable: '--color-urgent-muted',
                className: 'bg-urgent-muted',
            },
            foreground: {
                variable: '--color-urgent-muted-foreground',
                className: 'text-urgent-muted',
            },
            decorative: {
                variable: '--color-urgent-muted-decorative',
                className: 'text-urgent-muted-decorative',
            },
        },
    },
    info: {
        default: {
            surface: { variable: '--color-info', className: 'bg-info' },
            foreground: {
                variable: '--color-info-foreground',
                className: 'text-info',
            },
            decorative: {
                variable: '--color-info-decorative',
                className: 'text-info-decorative',
            },
        },
        muted: {
            surface: {
                variable: '--color-info-muted',
                className: 'bg-info-muted',
            },
            foreground: {
                variable: '--color-info-muted-foreground',
                className: 'text-info-muted',
            },
            decorative: {
                variable: '--color-info-muted-decorative',
                className: 'text-info-muted-decorative',
            },
        },
    },
    success: {
        default: {
            surface: { variable: '--color-success', className: 'bg-success' },
            foreground: {
                variable: '--color-success-foreground',
                className: 'text-success',
            },
            decorative: {
                variable: '--color-success-decorative',
                className: 'text-success-decorative',
            },
        },
        muted: {
            surface: {
                variable: '--color-success-muted',
                className: 'bg-success-muted',
            },
            foreground: {
                variable: '--color-success-muted-foreground',
                className: 'text-success-muted',
            },
            decorative: {
                variable: '--color-success-muted-decorative',
                className: 'text-success-muted-decorative',
            },
        },
    },
} as const satisfies Readonly<
    Record<ContextFamily, Readonly<Record<ContextTone, ContextColors>>>
>;

export const FOREGROUND_STYLES = {
    ordinary: { variable: '--color-foreground', className: 'text-foreground' },
    inverted: {
        variable: '--color-foreground-inverted',
        className: 'text-inverted',
    },
} as const satisfies Readonly<
    Record<'ordinary' | 'inverted', ContextStyle<`text-${string}`>>
>;

export function resolveColorContext(
    family: ContextFamily,
    tone: ContextTone = 'default'
): ContextColors {
    return CONTEXT_COLORS[family][tone];
}

/** Returns a deterministic item from a caller-owned ordered palette. */
export function getHashListItem<T>(value: string, items: readonly T[]): T {
    if (items.length === 0) {
        throw new Error('getHashListItem requires at least one item');
    }
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
        hash = Math.trunc((hash << 5) - hash + value.charCodeAt(i));
    }
    return items[Math.abs(hash) % items.length] as T;
}
